// The order the registration happens in, and the wire it happens over.
//
// ========================= THE FORK: CODE FIRST, THEN THE FORM. CHOSEN, NOT DRIFTED INTO.
//
// The two options were: ask for the details and then verify the address, or
// verify the address and then ask for the details. This surface does the second.
//
// WHAT IT COSTS, stated first because it is the real cost and it is not small.
// Completion drops. Somebody standing at a stand has to stop, leave the page,
// open a mail client, come back and type six digits before they have entered a
// single thing they came to enter. Some of them will not come back, and every
// one of those is a registration the client paid for and did not get. Form-first
// would measurably beat it.
//
// WHY IT IS STILL RIGHT.
//
//   FORM-FIRST MEANS HOLDING A STRANGER'S NAME, COMPANY, JOB TITLE AND PHONE
//   NUMBER FOR SOMEBODY WHO NEVER CONFIRMED ANYTHING. That is personal data,
//   collected, stored and now subject to every obligation that follows — and
//   collected from a person who, on the evidence, chose not to complete. Nobody
//   has decided how long it is kept, who may see it, whether it appears in an
//   export, or what a client is told about it. Code-first means the question
//   does not arise, and a question that does not arise cannot be answered wrong
//   for a year before anybody notices.
//
//   IT WOULD ALSO CREATE A RECORD WHOSE STATUS IS NEITHER "REGISTERED" NOR
//   "ABSENT", and this system has a strong opinion about those. The spine's
//   `registration_started` without a `registration_completed` already means
//   "abandoned", and it is a number clients look at. Form-first would fill that
//   number with people who typed an address they do not control and with
//   scripts, and there would be no way to tell those from a real person who
//   walked away — so the one metric that measures whether the stand is working
//   would stop measuring it.
//
//   AND THE FRICTION IS NOT ONLY A COST. The code is what makes the registration
//   worth having. A client's list of unverified addresses is worth close to
//   nothing to them; the friction and the quality are the same mechanism seen
//   from two sides.
//
// THE RETENTION CONSEQUENCE, WHICH IS THE THING TO WRITE DOWN. Under this order
// the only thing held for a person who does not finish is one row in
// `attendee_email_verification`: a digest of their address (not the address), a
// source IP, and two timestamps. It is swept within a day —
// ATTENDEE_VERIFICATION_RETENTION_SECONDS in api/src/attend/verify.ts — and it
// is not in the spine, not in any export, and not visible to a client. That is
// the whole of what abandonment costs an attendee here, and it is small enough
// to state in the privacy notice in one sentence.
//
// WHAT IS STILL OPEN: what happens to a *verified* person who abandons the form.
// Under this order that person has proved a mailbox and typed nothing, so the
// answer is the same sweep. It changes the day the registration write exists and
// `registration_started` begins to be emitted; xp/WEB-REPORT.md says so.
//
// ================================================ WHERE `registration_started` GOES
//
// After verification, not before. On the tablet it is written the moment the
// form appears, so that abandonment is measurable. Here the form does not appear
// until the mailbox is proved, so the same rule puts it after the code.
//
// THE CONSEQUENCE IS THAT WEB AND TABLET ABANDONMENT RATES ARE NOT THE SAME
// MEASUREMENT and must not be put in one column. The tablet's denominator is
// "people a crew member handed a device to". This surface's is "people who
// proved a mailbox". The second is a smaller, cleaner and more flattering
// number, and a report that averaged them would tell a client their stand
// improved when it did nothing of the kind.

import type { EnvironmentReading } from "./build/environment.js";

// ------------------------------------------------------------------- the wire

/**
 * The two urls, restated rather than imported.
 *
 * xp/web does not depend on xp/api and must not: one is a browser bundle and the
 * other is a Fastify service with a Postgres driver in it. check.mjs reads
 * api/src/attend/endpoints.ts off disk and compares the strings, which is the
 * same arrangement xp/kiosk uses for the tablet's environment table and is what
 * makes this a checked copy rather than a drift.
 */
export const ATTEND_ENDPOINTS = {
  requestCode: "/v1/attend/verification-codes",
  verifyCode: "/v1/attend/verifications",
  register: "/v1/attend/registrations",
  /**
   * The consent wording read, with the server's own parameter spelling in it.
   *
   * `:activationId` is carried rather than stripped so that this constant is
   * comparable to the server's by string equality, which is what makes the copy
   * checkable — and check.mjs now genuinely does that comparison. It did not
   * before: the comment above this object claimed a check read
   * api/src/attend/endpoints.ts off disk and compared the strings, and no such
   * assertion existed anywhere in this package. The claim is now true.
   *
   * `consentWordingPath` below is what a request is built from. A caller that
   * used this string directly would ask the server for a stand literally named
   * ":activationId", which answers the same 404 as everything else and would
   * therefore be indistinguishable from a shut stand — so the interpolation is
   * not left to a call site.
   */
  consentWording: "/v1/attend/activations/:activationId/consent-wording",
  onsiteRegister: "/v1/attend/onsite-registrations",
  wheelSpin: "/v1/attend/wheel-spins",
} as const;

/**
 * The path for one activation's wording.
 *
 * `encodeURIComponent` on a value that comes out of the address bar. The server
 * refuses anything that is not a uuid, so this cannot be used to reach another
 * route — but a caller who put a slash in the address bar would otherwise build
 * a url naming a different path entirely, and the refusal would be about the
 * wrong thing.
 */
export function consentWordingPath(activationId: string): string {
  return ATTEND_ENDPOINTS.consentWording.replace(":activationId", encodeURIComponent(activationId));
}

export type OnsiteRegisterBody = {
  activation_id: string;
  /** `/s/` is registration; `/sw/` is wheel. */
  lane: "registration" | "wheel";
  display_name: string | null;
  phone: string | null;
  config_version: number;
  field_set_version: number;
  fields: Readonly<Record<string, unknown>>;
  consent: readonly ConsentGrant[];
};

/**
 * `lane` is which of the two public paths this page is running.
 *
 * `/j/` and `/w/` are both live on a stand at once, so the server cannot work
 * it out from the activation. It decides which path the emailed link comes back
 * to, and on the register body whether this registration ends in a spin.
 */
export type PublicLane = "registration" | "wheel";

export type CodeRequestBody = { activation_id: string; email: string; lane?: PublicLane };
export type VerifyBody = { activation_id: string; email: string; code: string };

/**
 * A consent, as the page sends it.
 *
 * The hash of the rendered text travels rather than the text, on the same terms
 * as everywhere else in XP1: 005 holds the body centrally, keyed by
 * sha256(body), and the database refuses a consent citing a hash it cannot
 * produce. So the page cannot invent wording, and an export eight months later
 * can produce the exact paragraph somebody agreed to.
 *
 * There is no `granted: false`. A refusal is the absence of a grant, which is
 * the rule the consent_captured payload states — a person who declined and a
 * person who was never asked are different facts, and only one of them is
 * evidence of anything.
 */
export type ConsentGrant = {
  purpose: string;
  consent_version_id: string;
  wording_hash: string;
};

/**
 * The version of this stand's configuration the page was drawn from.
 *
 * SENT BECAUSE THIS PAGE IS THE ONLY THING THAT KNOWS IT. The server can find
 * out what is live at the moment it writes; only the browser knows which bundle
 * the person in front of it actually read, and those differ by however long
 * somebody spends on the form. api/src/attend/register.ts carries the argument.
 *
 * IT IS ALSO WHAT STOPS THIS PAGE BEING THE ONLY CONTROL. Until this key
 * existed, a stand with no published bundle served an empty consent list and the
 * server accepted a registration against it — an empty `consent` array is a
 * valid request there and means "agreed to nothing", which is the right shape
 * for somebody who declined and the wrong one for somebody who was never asked.
 * The refusal lived here, in a browser, drawn on a stranger's phone. It now
 * lives on the server as well, and this page refusing first is a courtesy rather
 * than the guarantee.
 */
export type RegisterBody = {
  activation_id: string;
  ticket: string;
  email: string;
  /** See PublicLane. Absent on the hall tablet, which has no public path. */
  lane?: PublicLane;
  display_name: string | null;
  phone: string | null;
  config_version: number;
  /**
   * The instruction the fields on this screen came out of.
   *
   * Zero when the wording read served none. A positive number is the one the
   * page was drawn from, not whichever is live now — the same argument
   * `config_version` makes. api/src/attend/register.ts proves it against the
   * cited bundle's instruction.
   */
  field_set_version: number;
  /**
   * Answers keyed by `field_id`. The mailbox is not sent here: it is the
   * address the ticket already proved.
   */
  fields: Readonly<Record<string, unknown>>;
  consent: readonly ConsentGrant[];
};

export type RegisterAccepted = {
  attendee_ref: string;
  event_id: string;
  registered_at: string;
  /** Present when the registration came in through the wheel lane. One use. */
  spin_ticket?: string;
};

export type SpinAccepted = {
  index: number;
  label: string;
  prize: boolean;
  claim: string;
};

export type VerifyAccepted = {
  ticket: string;
  ticket_expires_at: string;
  verification_id: string;
};

// ------------------------------------------------------------------ the stages

/**
 * Where somebody is, and there is no way to be in two of these.
 *
 * A union rather than a set of booleans, because the failure a set of booleans
 * produces on this surface is specific: `verified && !hasTicket` is a form
 * somebody can fill in and cannot submit, and it renders identically to the one
 * they can.
 */
export type Stage =
  /** The link did not name a stand. Nothing else can happen. */
  | { readonly at: "unusable"; readonly problem: string }
  /**
   * The welcome screen. Public only. Onsite never lands here: a hall tablet
   * has no mailbox gate and does not use this table.
   */
  | { readonly at: "start" }
  /** Asking for the address. Nothing has been stored and nothing has been sent. */
  | { readonly at: "address" }
  /** A code has been asked for. The address is held in memory only. */
  | { readonly at: "code"; readonly email: string }
  /** The mailbox is proved. This is the first stage at which anything is stored. */
  | { readonly at: "form"; readonly email: string; readonly proof: VerifyAccepted }
  /** Submitted, waiting. */
  | { readonly at: "writing"; readonly email: string; readonly proof: VerifyAccepted }
  | { readonly at: "done" }
  /** A named refusal, with what to do next. Never a bare failure. */
  | { readonly at: "refused"; readonly code: string; readonly problem: string };

/**
 * Which stages may follow which.
 *
 * Written as data rather than as the `if`s in a component, so that the ordering
 * decision at the top of this file is a thing a check can read. A component can
 * be wrong about one transition; this table is wrong about all of them or none.
 *
 * THE ENTRY THAT MATTERS IS `address -> code -> form`, WITH NO `address -> form`
 * AND NO `start -> form`. That absence is the whole ruling. If a shortcut is
 * ever added, the page would still look correct — the form renders, the fields
 * work — and the only thing that would have changed is that personal data is now
 * being collected before an address was proved.
 *
 * ONSITE IS NOT A ROW IN THIS TABLE. A hall tablet has no mailbox to prove and
 * does not share this page. Adding `start -> form` here would be that product
 * arriving as a transition, which is how a photographed QR becomes a form-first
 * public registration.
 */
export const TRANSITIONS: Readonly<Record<Stage["at"], readonly Stage["at"][]>> = {
  unusable: [],
  start: ["address"],
  address: ["code", "refused"],
  // Back to `address` because somebody who mistyped their address has to be able
  // to correct it without reloading. A resend stays on this stage and uses the
  // same address; it is not a transition. "Wrong address" is how a mistype is
  // fixed. The two are different things and both are on the code screen.
  code: ["address", "form", "refused"],
  form: ["writing", "refused"],
  writing: ["done", "refused"],
  done: [],
  refused: ["address"],
};

export function mayFollow(from: Stage["at"], to: Stage["at"]): boolean {
  return TRANSITIONS[from].includes(to);
}

/**
 * Whether anything at all is stored on the device at this stage.
 *
 * True in exactly one place, and check.mjs asserts that. The point is not the
 * boolean; it is that "when does this surface first touch the attendee's phone"
 * is answerable by reading four lines rather than by auditing a component tree.
 */
export function storesAnything(stage: Stage["at"]): boolean {
  return stage === "form" || stage === "writing";
}

// --------------------------------------------------------------- the transport

export type Transport = (
  url: string,
  /**
   * `body` optional, because the consent wording read is a GET and a GET with a
   * body is refused by fetch itself. Optional rather than `string | null` so
   * that the three posts below are unchanged and the one read simply omits it.
   */
  init: { method: string; headers: Record<string, string>; body?: string },
) => Promise<{ status: number; json: () => Promise<unknown> }>;

export type Refusal = { readonly code: string; readonly problem: string };

/**
 * What a bad venue network does, said plainly rather than pretended about.
 *
 * THE TABLET SURVIVES A DEAD NETWORK AND THIS CANNOT, AND THAT IS NOT A GAP TO
 * BE CLOSED LATER. xp/app writes to a local database on a device Nova owns and
 * syncs when it can, which works because the device is enrolled, the data is
 * signed, and the crew get the tablet back. None of those hold here: the page is
 * on somebody else's phone, they walk away with it, and there is no key to sign
 * anything with. An offline queue on this surface would be an attendee's
 * personal data sitting in a stranger's browser storage waiting for a
 * connection that may never come while they are still at the show — which is
 * both a worse privacy position than not collecting it and a registration the
 * client never receives anyway.
 *
 * So: this surface requires a working connection, and when there is not one it
 * says so and says what to do. The thing to do is real and is the reason it can
 * be said without embarrassment — there is a crew member with a tablet a few
 * metres away, and that tablet does work offline.
 */
export const NO_NETWORK: Refusal = {
  code: "no_network",
  problem:
    "This page could not reach the server. Venue wifi is often the problem. Try again in a moment, or " +
    "ask somebody on the stand to register you on their tablet — that works without a connection.",
};

const jsonHeaders = { "content-type": "application/json" };

/**
 * Read this stand's consent wording, before anything is asked of anybody.
 *
 * THE FIRST REQUEST THE PAGE MAKES, AND IT IS MADE BEFORE THE ADDRESS FIELD IS
 * DRAWN. Not because it is cheap, but because of what the alternative does: a
 * page that asked for an email address, sent a code, took the six digits and
 * then discovered it had no paragraph to show would have made somebody leave the
 * page, open their mail and come back, in order to be told the stand cannot
 * register them. Everything this request can refuse is a fact about the stand
 * rather than about the person, so it is knowable before the person is involved,
 * and knowing it first costs one round trip on a page load.
 *
 * IT SENDS NO CONTENT-TYPE AND NO BODY, so the request stays a CORS simple
 * request and needs no preflight. That is worth a line: `content-type:
 * application/json` is what makes the other three routes preflight, and adding
 * it here out of symmetry would double the latency of the page's first paint on
 * a venue connection to describe a body that does not exist.
 *
 * THE REFUSAL IS PASSED THROUGH AS THE SERVER WORDED IT, on refusalFrom's
 * reasoning, and that matters more here than on the three posts. The server
 * answers one sentence for a stand that is shut, a stand that has finished, a
 * stand from another environment and a stand that does not exist — deliberately,
 * so that the four are indistinguishable — and any attempt to be more specific
 * about which one it is would be this page reintroducing the distinction the
 * server went to some trouble to remove.
 */
export async function fetchConsentWording(
  transport: Transport,
  baseUrl: string,
  activationId: string,
  lane: "public" | "onsite" = "public",
): Promise<{ ok: true; served: unknown } | { ok: false; refusal: Refusal }> {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    const path = consentWordingPath(activationId);
    const url = lane === "onsite" ? `${baseUrl}${path}?lane=onsite` : `${baseUrl}${path}`;
    response = await transport(url, {
      method: "GET",
      headers: {},
    });
  } catch {
    return { ok: false, refusal: NO_NETWORK };
  }

  if (response.status !== 200) return { ok: false, refusal: await refusalFrom(response) };

  // Returned as `unknown` rather than cast, and the shape is checked in
  // privacy/notices.ts rather than here. The division is deliberate: this file
  // knows about the wire and that file knows what makes a consent presentable,
  // and a cast here would let a malformed body reach the checks in that file
  // already wearing the type they exist to establish.
  try {
    return { ok: true, served: await response.json() };
  } catch {
    return {
      ok: false,
      refusal: {
        code: "malformed_wording",
        problem:
          "The server answered this stand's consent wording with something this page cannot read, so it " +
          "will not ask you for anything. Somebody on the stand can register you.",
      },
    };
  }
}

/**
 * Ask for a code.
 *
 * Returns nothing on success, deliberately. The response body carries no
 * information about whether a mail was sent and this function must not invent
 * any: a caller holding a boolean would eventually branch on it, and the branch
 * would be visible to somebody probing which addresses have been here before.
 */
export async function askForCode(
  transport: Transport,
  baseUrl: string,
  body: CodeRequestBody,
): Promise<Refusal | null> {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    response = await transport(`${baseUrl}${ATTEND_ENDPOINTS.requestCode}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
  } catch {
    return NO_NETWORK;
  }

  if (response.status === 202) return null;
  return await refusalFrom(response);
}

export async function spendCode(
  transport: Transport,
  baseUrl: string,
  body: VerifyBody,
): Promise<{ ok: true; proof: VerifyAccepted } | { ok: false; refusal: Refusal }> {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    response = await transport(`${baseUrl}${ATTEND_ENDPOINTS.verifyCode}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, refusal: NO_NETWORK };
  }

  if (response.status !== 201) return { ok: false, refusal: await refusalFrom(response) };

  const payload = (await response.json()) as Partial<VerifyAccepted>;

  // Checked rather than trusted, even though the server is ours. A 201 whose
  // body is missing the ticket would otherwise put the page into the form stage
  // holding `undefined`, and the failure would surface at submission — after the
  // attendee has typed everything — as an error nobody can explain.
  if (typeof payload.ticket !== "string" || typeof payload.ticket_expires_at !== "string") {
    return {
      ok: false,
      refusal: {
        code: "malformed_confirmation",
        problem:
          "The server accepted the code and sent back something this page does not understand. Ask " +
          "somebody on the stand to register you.",
      },
    };
  }

  return {
    ok: true,
    proof: {
      ticket: payload.ticket,
      ticket_expires_at: payload.ticket_expires_at,
      verification_id: typeof payload.verification_id === "string" ? payload.verification_id : "",
    },
  };
}

/**
 * Spend the ticket and write the registration.
 *
 * NO RETRY, AND THAT IS THE POINT. A failed submission looks exactly like a
 * timed-out one from here, and the ticket is spent once: a page that retried
 * automatically would, on a slow venue connection where the first request
 * landed and its response did not, be asking the server to write a second
 * registration with a ticket that is already gone. The server would refuse it —
 * that is what the atomic spend is for — but the attendee would be shown a
 * failure for a registration that succeeded, which is the worst of the three
 * outcomes.
 *
 * So a refusal here is shown to the person with what to do next, and the retry
 * is a human pressing a button, having read a sentence.
 */
export async function submitRegistration(
  transport: Transport,
  baseUrl: string,
  body: RegisterBody,
): Promise<{ ok: true; written: RegisterAccepted } | { ok: false; refusal: Refusal }> {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    response = await transport(`${baseUrl}${ATTEND_ENDPOINTS.register}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, refusal: NO_NETWORK };
  }

  if (response.status !== 201) return { ok: false, refusal: await refusalFrom(response) };

  const payload = (await response.json()) as Partial<RegisterAccepted>;

  // Checked for the same reason spendCode checks its body, with one difference
  // that matters: by this point the registration has been written. So a
  // malformed 201 must not read as a failure — telling somebody their
  // registration did not save when it did is worse than telling them nothing.
  if (typeof payload.attendee_ref !== "string") {
    return {
      ok: true,
      written: { attendee_ref: "", event_id: "", registered_at: "" },
    };
  }

  return {
    ok: true,
    written: acceptedFrom(payload),
  };
}

export async function submitOnsiteRegistration(
  transport: Transport,
  baseUrl: string,
  body: OnsiteRegisterBody,
): Promise<{ ok: true; written: RegisterAccepted } | { ok: false; refusal: Refusal }> {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    response = await transport(`${baseUrl}${ATTEND_ENDPOINTS.onsiteRegister}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, refusal: NO_NETWORK };
  }

  if (response.status !== 201) return { ok: false, refusal: await refusalFrom(response) };

  const payload = (await response.json()) as Partial<RegisterAccepted>;
  if (typeof payload.attendee_ref !== "string") {
    return {
      ok: true,
      written: { attendee_ref: "", event_id: "", registered_at: "" },
    };
  }

  return {
    ok: true,
    written: acceptedFrom(payload),
  };
}

export async function spinWheel(
  transport: Transport,
  baseUrl: string,
  body: { activation_id: string; ticket: string },
): Promise<{ ok: true; landed: SpinAccepted } | { ok: false; refusal: Refusal }> {
  let response: { status: number; json: () => Promise<unknown> };
  try {
    response = await transport(`${baseUrl}${ATTEND_ENDPOINTS.wheelSpin}`, {
      method: "POST",
      headers: jsonHeaders,
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, refusal: NO_NETWORK };
  }

  if (response.status !== 201) return { ok: false, refusal: await refusalFrom(response) };

  const payload = (await response.json()) as Partial<SpinAccepted>;
  if (typeof payload.index !== "number" || !Number.isInteger(payload.index) || payload.index < 0) {
    return {
      ok: false,
      refusal: {
        code: "unavailable",
        problem: "the wheel could not be started. Ask somebody on the stand.",
      },
    };
  }

  return {
    ok: true,
    landed: {
      index: payload.index,
      label: typeof payload.label === "string" ? payload.label : "",
      prize: payload.prize === true,
      claim: typeof payload.claim === "string" ? payload.claim : "",
    },
  };
}

function acceptedFrom(payload: Partial<RegisterAccepted>): RegisterAccepted {
  return {
    attendee_ref: typeof payload.attendee_ref === "string" ? payload.attendee_ref : "",
    event_id: typeof payload.event_id === "string" ? payload.event_id : "",
    registered_at: typeof payload.registered_at === "string" ? payload.registered_at : "",
    ...(typeof payload.spin_ticket === "string" && payload.spin_ticket !== ""
      ? { spin_ticket: payload.spin_ticket }
      : {}),
  };
}

/**
 * A refusal from a response, named by the server where the server named it.
 *
 * The server's `error` is used as the code and the server's `detail` as the
 * words, because api/src/attend/routes.ts writes those to be read by a person
 * and rewriting them here would be two sets of wording to keep true. What this
 * adds is a floor: a response with no usable body still produces a named
 * refusal, because "something went wrong" with no name is the thing this
 * codebase does not ship.
 */
async function refusalFrom(response: { status: number; json: () => Promise<unknown> }): Promise<Refusal> {
  let body: { error?: unknown; detail?: unknown } = {};
  try {
    body = (await response.json()) as typeof body;
  } catch {
    body = {};
  }

  const code = typeof body.error === "string" ? body.error : `http_${response.status}`;
  const detail =
    typeof body.detail === "string"
      ? body.detail
      : "The server refused this and did not say why. Ask somebody on the stand to register you.";

  return { code, problem: detail };
}

/**
 * The address to talk to, or a refusal.
 *
 * A function rather than a constant because `readEnvironment` can fail, and the
 * page has to be able to render that failure rather than throwing during a
 * render. It cannot happen in a shipped build — vite.config.ts refuses one — and
 * it is handled anyway, on the two-locks rule the kiosk states.
 */
export function serverFor(reading: EnvironmentReading): { baseUrl: string } | Refusal {
  if (!reading.ok) return { code: "no_server", problem: reading.problem };
  return { baseUrl: reading.baseUrl };
}
